import type { ReactNode } from 'react';
import type { HubId } from '../../nav/hubs';
import { CustomersPage } from '../accounts/CustomersPage';
import { DocumentsPage } from '../accounts/DocumentsPage';
import { DossiersPage } from '../accounts/DossiersPage';
import { DocumentTypesPage } from '../declarations/DocumentTypesPage';
import { CaptchaPage } from '../integrations/CaptchaPage';
import { ModulesPage } from '../modules/ModulesPage';
import { SmartCaPage } from '../integrations/SmartCaPage';
import { SubjectsPage } from '../accounts/SubjectsPage';
import { ApprovalsPage } from '../admin/ApprovalsPage';
import { AuditPage } from '../admin/AuditPage';
import { CredentialsPage } from '../admin/CredentialsPage';
import { RolesPage } from '../admin/RolesPage';
import { StaffPage } from '../admin/StaffPage';
import { BillingPage } from '../commerce/BillingPage';
import { CatalogPage } from '../catalog/CatalogPage';
import { SubscriptionsPage } from '../subscriptions/SubscriptionsPage';
import { LocationsPage, ServerGroupsPage, ServiceQuotePage } from '../catalog/SetupPages';
import { PriceRulesPage, PromotionScopesPage } from '../catalog/PricingScreens';
import { InvoicesPage } from '../commerce/InvoicesPage';
import { LedgerPage } from '../commerce/LedgerPage';
import { OrdersPage } from '../commerce/OrdersPage';
import { BankTransactionsPage } from '../commerce/BankTransactionsPage';
import { PaymentsPage } from '../commerce/PaymentsPage';
import { PricingPage } from '../commerce/PricingPage';
import { PromotionsPage } from '../commerce/PromotionsPage';
import { QuotesPage } from '../commerce/QuotesPage';
import { AbuseCasesPage } from '../compliance/AbuseCasesPage';
import { DisclosurePage } from '../compliance/DisclosurePage';
import { DisputesPage } from '../compliance/DisputesPage';
import { VnnicCoveragePage } from '../compliance/vnnic/CoveragePage';
import { VnnicDossierSyncPage } from '../compliance/vnnic/DossierSyncPage';
import { PostOperationsPage } from '../compliance/vnnic/PostOperationsPage';
import { TrackingReportsPage } from '../compliance/vnnic/TrackingReportsPage';
import { RegistrarSignaturePage } from '../compliance/vnnic/RegistrarSignaturePage';
import { VnnicInternationalReportsPage } from '../compliance/vnnic/InternationalReportsPage';
import { ContactsPage } from '../core/ContactsPage';
import { RegistrantChangesPage } from '../core/RegistrantChangesPage';
import { TransfersPage } from '../core/TransfersPage';
import { DomainLookupPage } from '../domains/DomainLookupPage';
import { DomainsPage } from '../domains/DomainsPage';
import { DeadlinesPage } from '../governance/DeadlinesPage';
import { PoliciesPage } from '../governance/PoliciesPage';
import { ConnectionsPage } from '../integrations/ConnectionsPage';
import { OteTestsPanel } from '../integrations/OteTestsPanel';
import { TldChannelsPage } from '../integrations/TldChannelsPage';
import { VnnicPage } from '../integrations/VnnicPage';
import { ReportingApiPage } from '../integrations/reporting/ReportingApiPage';
import { AuctionBidsPage } from '../market/auctions/BidsPage';
import { AuctionHandoverPage } from '../market/auctions/HandoverPage';
import { AuctionMarketPage } from '../market/auctions/MarketPage';
import { AuctionSessionsPage } from '../market/auctions/SessionsPage';
import { AuctionSettlementPage } from '../market/auctions/SettlementPage';
import { CatchingPage } from '../market/backorder/CatchingPage';
import { BackorderHoldsPage } from '../market/backorder/HoldsPage';
import { BackorderRequestsPage, DropListPage } from '../market/backorder/ListPages';
import { BackorderOverviewPage } from '../market/backorder/OverviewPage';
import { CaseInboxPage } from '../market/compliance/CaseInboxPage';
import { LegalLocksPage } from '../market/compliance/LegalLocksPage';
import { RdapPage } from '../market/compliance/RdapPage';
import { RdePage } from '../market/compliance/RdePage';
import { VnnicCorrectionsPage } from '../market/compliance/VnnicCorrectionsPage';
import { EmailPage } from '../notify/EmailPage';
import { NotificationLogPage } from '../notify/NotificationLogPage';
import { ZaloPage } from '../notify/ZaloPage';
import { AlertsPage } from '../ops/AlertsPage';
import { IncidentsPage } from '../ops/IncidentsPage';
import { LogRetentionPage } from '../ops/LogRetentionPage';
import { LogsPage } from '../ops/LogsPage';
import { MetricsPage } from '../ops/MetricsPage';
import { OpsOverviewPage } from '../ops/OpsOverviewPage';
import { QueuesPage } from '../ops/QueuesPage';
import { OpsRegistriesPage } from '../ops/RegistriesPage';
import { ServicesPage } from '../ops/ServicesPage';
import { SloPage } from '../ops/SloPage';
import { TracesPage } from '../ops/TracesPage';
import { WorkflowsPage } from '../ops/workflows/WorkflowsPage';
import { EppPage } from '../registry/EppPage';
import { PollPage } from '../registry/PollPage';
import { ReconciliationPage } from '../registry/ReconciliationPage';
import { RegistriesPage } from '../registry/RegistriesPage';
import { SessionsPage } from '../registry/SessionsPage';
import { TldCatalogPage } from '../registry/TldCatalogPage';
import { TldsPage } from '../registry/TldsPage';
import { TransactionsPage } from '../registry/TransactionsPage';
import { DomainServicesPage } from '../services/DomainServicesPage';
import { QuotePage } from '../services/QuotePage';
import { NameChangesPage } from '../services/NameChangesPage';
import { ProtectedPage } from '../services/ProtectedPage';
import { RequirementSetsPage } from '../services/RequirementSetsPage';
import { DeclarationTemplatesPage } from '../declarations/DeclarationTemplatesPage';
import { TrustAnchorsPage } from '../declarations/TrustAnchorsPage';
import { DeclarationChecksPage } from '../declarations/DeclarationChecksPage';
import { VnZonesPage } from '../declarations/VnZones';
import { TldSettingsPage } from '../services/TldSettingsPage';
import { WebhooksPage } from '../work';
import { DossierQueuePage } from '../work/DossierQueuePage';
import { WorkQueuePage } from '../work/WorkQueuePage';
import { DomainQuickFilters } from './DomainQuickFilters';
import { SettingsIndexPage } from './SettingsIndexPage';

/**
 * What each hub tab shows, keyed `<hub>/<tab>` or `<hub>/<tab>/<sub>` (ids in `nav/hubs.ts`).
 * Every entry is an existing page, reused as it is; the hub gives it the tab bar.
 */
export const HUB_CONTENT: Readonly<Record<string, ReactNode>> = {
  'home/dossiers': <DossierQueuePage />,
  'home/queue': <WorkQueuePage />,
  'home/protected': <ProtectedPage />,
  'home/approvals': <ApprovalsPage />,
  'home/deadlines': <DeadlinesPage />,
  'home/requests': <NameChangesPage />,

  'domains/list': (
    <>
      <DomainQuickFilters />
      <DomainsPage />
    </>
  ),
  'domains/lookup': <DomainLookupPage />,
  'domains/transfers': <TransfersPage />,
  'domains/registrant-changes': <RegistrantChangesPage />,
  'domains/contacts': <ContactsPage />,

  'customers/customers': <CustomersPage />,
  'customers/subjects': <SubjectsPage />,
  'customers/dossiers': <DossiersPage />,
  'customers/documents': <DocumentsPage />,

  'sales/quotes': <QuotesPage />,
  'sales/orders': <OrdersPage />,
  'sales/invoices': <InvoicesPage />,
  'sales/payments': <PaymentsPage />,
  'sales/bank-transactions': <BankTransactionsPage />,
  'sales/ledger': <LedgerPage />,
  'sales/subscriptions': <SubscriptionsPage />,
  'sales/overview': <BillingPage />,

  'products/tlds': <TldsPage />,
  'products/catalog': <TldCatalogPage />,
  'products/pricing': <PricingPage part="prices" />,
  'products/services': <DomainServicesPage />,
  'products/promotions': <PromotionsPage />,
  'products/tax': <PricingPage part="tax" />,
  'products/quote': <QuotePage />,
  'products/product-catalog/products': <CatalogPage />,
  'products/product-catalog/server-groups': <ServerGroupsPage />,
  'products/product-catalog/locations': <LocationsPage />,
  'products/product-catalog/service-quote': <ServiceQuotePage />,
  'products/product-catalog/price-rules': <PriceRulesPage />,
  'products/product-catalog/promotion-scopes': <PromotionScopesPage />,

  'backorder/overview': <BackorderOverviewPage />,
  'backorder/drops': <DropListPage />,
  'backorder/requests': <BackorderRequestsPage />,
  'backorder/catching': <CatchingPage />,
  'backorder/holds': <BackorderHoldsPage />,

  'auctions/market': <AuctionMarketPage />,
  'auctions/sessions': <AuctionSessionsPage />,
  'auctions/bids': <AuctionBidsPage />,
  'auctions/settlement': <AuctionSettlementPage />,
  'auctions/handover': <AuctionHandoverPage />,

  'compliance/vnnic/dossier-sync': <VnnicDossierSyncPage />,
  'compliance/vnnic/international-reports': <VnnicInternationalReportsPage />,
  'compliance/vnnic/coverage': <VnnicCoveragePage />,
  'compliance/vnnic/corrections': <VnnicCorrectionsPage />,
  'compliance/vnnic/post-operations': <PostOperationsPage />,
  'compliance/vnnic/registrar-signature': <RegistrarSignaturePage />,
  'compliance/vnnic/tracking-reports': <TrackingReportsPage />,
  'compliance/icann/rdap': <RdapPage />,
  'compliance/icann/rde': <RdePage />,
  'compliance/icann/disclosure': <DisclosurePage />,
  'compliance/cases/inbox': <CaseInboxPage />,
  'compliance/cases/abuse': <AbuseCasesPage />,
  'compliance/cases/disputes': <DisputesPage />,
  'compliance/cases/legal-locks': <LegalLocksPage />,
  'compliance/policies': <PoliciesPage />,

  'ops/overview': <OpsOverviewPage />,
  'ops/registry/registries': <RegistriesPage />,
  'ops/registry/transactions': <TransactionsPage />,
  'ops/registry/reconciliation': <ReconciliationPage />,
  'ops/registry/poll': <PollPage />,
  'ops/registry/sessions': <SessionsPage />,
  'ops/registry/epp': <EppPage />,
  'ops/registry/health': <OpsRegistriesPage />,
  'ops/services': <ServicesPage />,
  'ops/queues/queues': <QueuesPage />,
  'ops/queues/workflows': <WorkflowsPage />,
  'ops/alerts/alerts': <AlertsPage />,
  'ops/alerts/incidents': <IncidentsPage />,
  'ops/telemetry/metrics': <MetricsPage />,
  'ops/telemetry/logs': <LogsPage />,
  'ops/telemetry/traces': <TracesPage />,
  'ops/slo': <SloPage />,

  'settings/all': <SettingsIndexPage />,
  'settings/domains/channels': <TldChannelsPage />,
  'settings/domains/lifecycle': <TldSettingsPage part="terms" />,
  'settings/domains/parking': <TldSettingsPage part="parking" />,
  'settings/domains/requirements': <RequirementSetsPage />,
  'settings/domains/declarations': <DeclarationTemplatesPage />,
  'settings/domains/document-types': <DocumentTypesPage />,
  'settings/domains/trust-anchors': <TrustAnchorsPage />,
  'settings/domains/declaration-checks': <DeclarationChecksPage />,
  'settings/domains/vn-zones': <VnZonesPage />,
  'settings/connections/registries': <ConnectionsPage />,
  'settings/connections/vnnic': <VnnicPage />,
  'settings/connections/smartca': <SmartCaPage />,
  'settings/connections/captcha': <CaptchaPage />,
  'settings/connections/ote': <OteTestsPanel />,
  'settings/connections/reporting': <ReportingApiPage />,
  'settings/modules': <ModulesPage />,
  'settings/notify/email': <EmailPage />,
  'settings/notify/sent': <NotificationLogPage />,
  'settings/notify/zalo': <ZaloPage />,
  'settings/notify/webhooks': <WebhooksPage />,
  'settings/security/staff': <StaffPage />,
  'settings/security/roles': <RolesPage />,
  'settings/security/api-keys': <CredentialsPage />,
  'settings/security/audit': <AuditPage />,
  'settings/security/logs': <LogRetentionPage />,
};

export function hubContentKey(hub: HubId, tab: string, sub?: string): string {
  return sub ? `${hub}/${tab}/${sub}` : `${hub}/${tab}`;
}
